"use client";

import React, { useMemo } from "react";
import IndicatorTable from "@components/DetailsCommon/Forecast/TechnicalSignals/IndicatorTable";
import ForecastCard from "@components/DetailsCommon/Forecast/ForecastCard";
import { useStockDetailsMarket } from "@hooks/useStockDetailsMarket";
import { useStockDetailsHandle } from "@hooks/useStockDetailsHandle";
import { useTokenDetailsMarket } from "@hooks/useTokenDetailsMarket";
import { useTokenDetailsHandle } from "@hooks/useTokenDetailsHandle";
import { useTechnicalIndicators } from "@/src/hooks/useTechnicalIndicators";
import { FORECAST_QUERY_PARAMS } from "@config/stock";
import calculateTimeRange from "@utils/calculateTimeRange";
import {
  ViewDetailsLink,
  buildStockDetailModuleHref,
  buildCryptoDetailHref,
} from "./ViewDetailsLink";

interface AssetTechnicalSignalDetailBusinessChartProps {
  assetType: "stock" | "crypto";
  assetRouterId: string;
}

export const AssetTechnicalSignalDetailBusinessChart: React.FC<
  AssetTechnicalSignalDetailBusinessChartProps
> = ({ assetType, assetRouterId }) => {
  const isStock = assetType === "stock";

  const cryptoQueryParams = useMemo(() => {
    const { startTime, endTime } = calculateTimeRange("180d");
    return {
      "enrich.enable": true,
      "enrich.with_ohlc.enable": true,
      "enrich.with_ohlc.start_time": startTime,
      "enrich.with_ohlc.end_time": endTime,
      "enrich.with_ohlc.interval": "1d",
    };
  }, []);

  const { data: stockData, isLoading: stockLoading } = useStockDetailsMarket({
    enabled: isStock,
    queryParams: { ...FORECAST_QUERY_PARAMS, ids: [assetRouterId] },
    queryKey: ["chat-technical-detail", assetRouterId],
  });

  const { data: tokenList, isLoading: tokenLoading } = useTokenDetailsMarket({
    enabled: !isStock,
    ids: [assetRouterId],
    queryParams: cryptoQueryParams,
  });

  const isLoading = isStock ? stockLoading : tokenLoading;
  const stockSymbol = stockData?.symbol || "";

  const { ohlcv: stockOhlcv } = useStockDetailsHandle({
    tokenList: isStock ? stockData : undefined,
    symbol: stockSymbol,
  });

  const { ohlcv: tokenOhlcv, symbol: tokenSymbol } = useTokenDetailsHandle({
    tokenList: !isStock ? tokenList : undefined,
  });

  const ohlcv = isStock ? stockOhlcv : tokenOhlcv;
  const displaySymbol = isStock ? stockSymbol : tokenSymbol || assetRouterId;

  const { indicatorData } = useTechnicalIndicators({
    symbol: displaySymbol,
    isStock,
    ohlcv: ohlcv as any,
  });

  if (isLoading) {
    return (
      <div className="h-60 w-full animate-pulse bg-background_white_overlay5" />
    );
  }

  if (!indicatorData?.length) {
    return null;
  }

  const detailHref = isStock
    ? buildStockDetailModuleHref(stockSymbol, "forecast")
    : buildCryptoDetailHref(assetRouterId);

  return (
    <>
      <div className="w-full overflow-hidden">
        <ForecastCard>
          <IndicatorTable data={indicatorData} />
        </ForecastCard>
      </div>
      <ViewDetailsLink href={detailHref} />
    </>
  );
};
